import {Dialog, DialogActions, DialogContent} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {setSnackbar} from '../../store/reducers/globals';
import {useDispatch} from 'react-redux';
import Formatter from '../../util/formatter';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import WarningIcon from '@mui/icons-material/Warning';
import AgyleButton from '../ui/buttons/agyleButton';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

const useStyles = makeStyles((theme) => ({
    title: {
        padding: 10,
        border: '2px solid #7f4501',
        borderRadius: '8px 8px 0 0',
        color: '#7f4501',
        backgroundColor: '#fee4b1',
        fontWeight: 'bold',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    titleButton: {
        marginLeft: 'auto',
        color: '#7f4501',
    },
    background: {
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#313233',
    },
    warning: {
        padding: 10,
        border: '2px solid #7f4501',
        borderRadius: 8,
        color: '#7f4501',
        backgroundColor: '#fee4b1',
        gap: 10,
        fontWeight: 600,
        marginTop: 10,
    },
    patientReminder: {
        marginTop: 10,
        padding: 10,
        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#3d3e40',
        border: `1px solid ${theme.palette.mode === 'light' ? '#acafb3' : '#626466'}`,
        borderRadius: 8,
        color: theme.palette.mode === 'light' ? '#494b4c' : '#fff',
        fontWeight: 500,
    },
    spec: {
        marginLeft: 'auto',
    },
    ptDataType: {
        color: theme.palette.mode === 'light' ? '#626466' : '#acafb3',
        width: 100,
    },
    gender: {
        color: '#222425',
        borderRadius: 6,
        display: 'flex',
        padding: 1,
    },
    speciality: {
        backgroundColor: '#37588d',
        borderRadius: 6,
        padding: '2px 10px',
        fontSize: 'small',
    },
}));

const PatientDemographicWarning = ({open, onClose, onConfirm, patient, type}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const isSimilarName = type === 'similar';

    const handleCopyPtNumber = (number) => {
        if (!navigator.clipboard || !number) return;
        navigator.clipboard.writeText(number)
            .then(() => {
                dispatch(setSnackbar({
                    type: 'success',
                    message: 'The patient number has been copied to the clipboard.',
                }));
            });
    };

    const getAge = (dob) => {
        if (!dob) return 'Unknown';
        return Formatter.formatPatientAge(dob);
    };

    const getGender = () => {
        const gender = patient?.patient?.gender;
        let icon;
        let color;

        switch (gender) {
            case 'Female':
                icon = <FemaleIcon fontSize="small"/>;
                color = '#F4BDD1';
                break;
            case 'Male':
                icon = <MaleIcon fontSize="small"/>;
                color = '#92BFE2';
                break;
            default:
                icon = <QuestionMarkIcon fontSize="small"/>;
                color = '#acafb3';
        }

        return (
            <div className={classes.gender} style={{backgroundColor: color}}>
                {icon}
            </div>
        );
    };

    const getSpec = () => {
        if (!patient?.specialty?.speciality) return null;

        return (
            <div className={classes.speciality}>
                {patient?.specialty?.speciality?.__description}
            </div>
        );
    };

    const patientDemographic = () => {
        if (!open) return;

        return (
            <div className={classes.patientReminder}>
                {/* First line */}
                <div className={classes.divRow} style={{gap: 5, marginBottom: 10}}>
                    <span>
                        {`${patient?.patient?.familyName} ${patient?.patient?.firstName}`}
                    </span>

                    <span>
                        {getGender()}
                    </span>

                    <div className={classes.spec}>
                        {getSpec()}
                    </div>
                </div>

                {/* Age */}
                <div className={classes.divRow} style={{gap: 5}}>
                    <span className={classes.ptDataType}>
                        {t('Age')}
                    </span>

                    <span>
                       {getAge(patient?.patient?.dob)}
                    </span>
                </div>

                {/* Hospital no*/}
                <div
                    className={classes.divRow}
                    style={{gap: 5}}
                    onClick={handleCopyPtNumber.bind(this, patient?.patient?.hospitalNumber)}
                >
                    <span className={classes.ptDataType}>
                        {t('Hospital No.')}
                    </span>

                    <span style={{cursor: 'pointer'}}>
                        {patient?.patient?.hospitalNumber || 'Not provided'}
                    </span>
                </div>

                {/* NHS no */}
                <div
                    className={classes.divRow}
                    style={{gap: 5}}
                    onClick={handleCopyPtNumber.bind(this, patient?.patient?.nhsNumber)}
                >
                    <span className={classes.ptDataType}>
                        {t('NHS No.')}
                    </span>

                    <span style={{cursor: 'pointer'}}>
                        {patient?.patient?.nhsNumber || 'Not provided'}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableEscapeKeyDown
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    zIndex: isSimilarName ? 1500 : undefined,
                },
            }}
        >
            <DialogTitle className={classes.title}>
                <div className={classes.divRow} style={{gap: 10}}>
                    <WarningIcon/>

                    <span style={{fontSize: 16}}>
                        {t(
                            isSimilarName
                                ? 'Warning! - Patient with similar name on Whiteboard'
                                : 'Warning! - Current Patient View',
                        )}
                    </span>

                    <IconButton
                        className={classes.titleButton}
                        onClick={onClose}
                    >
                        <CloseIcon/>
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent className={classes.background}>
                {isSimilarName &&
                    <div className={clsx(classes.divRow, classes.warning)}>
                        <WarningIcon/>

                        <span>
                        {t('Please confirm this is patient you are looking for!')}
                    </span>
                    </div>
                }

                {patientDemographic()}
            </DialogContent>

            <DialogActions className={classes.background}>
                {isSimilarName &&
                    <AgyleButton
                        onClick={onClose}
                        width={85}
                        colour="primary"
                    >
                        {t('Close')}
                    </AgyleButton>
                }

                <AgyleButton
                    onClick={onConfirm}
                    variant="contained"
                    width={85}
                >
                    {t('Confirm')}
                </AgyleButton>
            </DialogActions>
        </Dialog>
    );
};

export default PatientDemographicWarning;